import { Database, Download, Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import ToastHelper from "@/utils/toastHelper";
import { adminDatabaseBackupService } from "@/services/adminDatabaseBackupService";

export default function AdminDatabaseBackup() {
  const [downloading, setDownloading] = useState(false);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await adminDatabaseBackupService.download();
      const timestamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
      const fallbackName = `GITLearningDB_Backup_${timestamp}.bak`;
      const disposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8''|\")?([^;\"]+)/i);
      const fileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1].trim()) : fallbackName;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      ToastHelper.success("Database backup downloaded.");
    } catch (error) {
      ToastHelper.error(error?.response?.data?.message || "Unable to create the database backup.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="mx-auto max-w-4xl space-y-6">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-orange-600">System administration</p>
        <h1 className="mt-1 text-2xl font-extrabold text-[#183050]">Database Backup</h1>
        <p className="mt-2 text-sm text-slate-600">Create and download a full SQL Server backup of the current database.</p>
      </div>

      <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#183050] text-white"><Database className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold text-[#183050]">Download database backup</h2>
            <p className="mt-1 text-sm text-slate-600">The backup will be downloaded as a `.bak` file with the current date and time in its filename.</p>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"><ShieldCheck className="h-4 w-4 shrink-0" />Available to Super Admin only.</div>
            <button type="button" onClick={downloadBackup} disabled={downloading} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-3 text-sm font-extrabold text-white shadow-sm transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? "Creating backup..." : "Download Backup"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
